import { Link } from "react-router-dom";
import { MessageSquareText, Mic, ImageIcon, ScanText, Headset, ShieldCheck, Lock, EyeOff, UserCheck, Download, ArrowRight, KeyRound, Timer } from "lucide-react";
import { AuroraBackground } from "@/components/AuroraBackground";
import { AuroraOrb } from "@/components/AuroraOrb";
import { Logo, ModelLogos } from "@/components/Brand";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { useInstallPrompt } from "@/lib/pwa";
import { useEffect, useState } from "react";
import { HeartToggle } from "@/components/HeartToggle";
import { InstallButtons } from "@/components/InstallButtons";
import { GroupProjects } from "@/components/GroupProjects";
import { useAppConfig } from "@/lib/config";

const FEATURES = [
  { Icon: MessageSquareText, t: "Chat multi-modello", d: "Gemini, GPT-5.4, Claude, Grok, Kimi e DeepSeek in un'unica interfaccia con cronologia." },
  { Icon: Mic, t: "Assistente vocale", d: "Parla con Aurora a mani libere: ti ascolta, ragiona e ti risponde a voce." },
  { Icon: ImageIcon, t: "Immagini AI", d: "Genera immagini con Nano Banana e conservale nella tua galleria privata." },
  { Icon: ScanText, t: "OCR multi-pagina", d: "Estrai testo da foto e scansioni, copialo o esportalo in PDF." },
  { Icon: Headset, t: "Supporto diretto", d: "Chat 1-a-1 con il team Aurora e notifiche push sul telefono." },
];

const SECURITY = [
  { Icon: Lock, t: "Crittografia AES-256", d: "Conversazioni e messaggi sono cifrati sul server con AES-256-GCM: illeggibili anche nel database." },
  { Icon: ShieldCheck, t: "TLS in transito", d: "Ogni comunicazione tra il tuo dispositivo e Aurora viaggia su HTTPS/TLS." },
  { Icon: KeyRound, t: "Password blindate", d: "Salvate solo come hash bcrypt con salt: nessuno può leggerle, nemmeno noi." },
  { Icon: Timer, t: "Autodistruzione", d: "Imposta la cancellazione automatica delle conversazioni dopo 24 ore, 7 o 30 giorni." },
  { Icon: EyeOff, t: "Zero vendita di dati", d: "Nessuna pubblicità, nessuna profilazione, nessuna cessione a terzi." },
  { Icon: UserCheck, t: "Controllo totale", d: "Elimina account e dati per sempre, in un tocco, quando vuoi." },
];

function useOrbSize() {
  const get = () => Math.min(560, Math.max(300, window.innerWidth * 0.92));
  const [s, setS] = useState(get);
  useEffect(() => { const f = () => setS(get()); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);
  return s;
}

export default function Landing() {
  const { user } = useAuth();
  const { can, install } = useInstallPrompt();
  const size = useOrbSize();
  const { subscriptions_enabled: paid } = useAppConfig();
  const startTo = user ? "/app" : "/registrati";

  return (
    <div className="min-h-screen text-slate-200">
      <AuroraBackground intense />
      <header data-testid="app-header" className="sticky top-0 z-50 backdrop-blur-2xl bg-[#030712]/80 border-b border-cyan-500/15">
        <div className="max-w-7xl mx-auto px-3 sm:px-8 py-3 sm:py-3.5 flex items-center justify-between gap-2">
          <Logo />
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <a href="#scarica" onClick={(e) => { if (can) { e.preventDefault(); install(); } }} data-testid="btn-install-app"
              className="flex items-center gap-1.5 shrink-0 whitespace-nowrap rounded-full px-2.5 py-1.5 sm:px-4 sm:py-2 text-[10px] sm:text-sm font-bold uppercase tracking-wide sm:tracking-wider text-[#1a1203] bg-gradient-to-r from-amber-300 to-yellow-400 shadow-[0_0_24px_rgba(251,191,36,.55)] hover:shadow-[0_0_36px_rgba(251,191,36,.8)] transition-shadow duration-300">
              <Download size={14} /> <span className="sm:hidden">App</span><span className="hidden sm:inline">Scarica APP</span>
            </a>
            <Link to={user ? "/app" : "/accedi"} data-testid="btn-login" className="btn-ghost-hud shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium">
              {user ? "Apri app" : "Accedi"}
            </Link>
          </div>
        </div>
      </header>

      <section className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-8 sm:pt-12 pb-16 flex flex-col items-center text-center">
        <p className="hud-tag fade-up flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" /> Sistema online · Core attivo</p>
        <div className="relative my-1 fade-up" style={{ animationDelay: ".15s" }}>
          <AuroraOrb size={size} />
          <img src="/logo.png" alt="" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[20%] rounded-2xl opacity-90 mix-blend-screen" />
        </div>
        <HeartToggle className="-mt-3 mb-3" />
        <h1 className="font-heading glow-title text-5xl sm:text-6xl lg:text-7xl font-bold tracking-[0.12em] text-white fade-up" style={{ animationDelay: ".3s" }} data-testid="hero-wordmark">
          AURORA <span className="text-aurora">ON</span>
        </h1>
        <p className="mt-3 font-heading text-lg md:text-xl tracking-[0.35em] uppercase text-cyan-200/90 fade-up" style={{ animationDelay: ".4s" }}>Il tuo Hub AI Unificato</p>
        <p className="mt-4 max-w-xl text-sm sm:text-base text-slate-400 fade-up" style={{ animationDelay: ".5s" }}>
          Tutti i migliori modelli di intelligenza artificiale, un'unica assistente vocale. Parla, scrivi, crea: Aurora risponde.
        </p>
        <div className="mt-7"><ModelLogos /></div>
        <div className="mt-9 flex flex-col sm:flex-row gap-3 fade-up" style={{ animationDelay: ".9s" }}>
          <Link to={startTo} data-testid="btn-start-now" className="btn-aurora rounded-full px-8 py-3.5 text-sm uppercase flex items-center justify-center gap-2">
            Inizia ora <ArrowRight size={16} />
          </Link>
          <Link to={user ? "/app" : "/accedi"} data-testid="btn-login-hero" className="btn-ghost-hud rounded-full px-8 py-3.5 text-sm uppercase tracking-wider">
            Accedi
          </Link>
        </div>
        <p className="mt-4 text-xs font-mono text-slate-500">{paid ? "30 giorni di prova gratuita · nessuna carta richiesta" : "Completamente gratuita · nessuna carta richiesta"}</p>
        <div id="scarica" className="mt-10 w-full scroll-mt-24">
          <p className="hud-tag mb-4">Installa Aurora ON sul telefono</p>
          <InstallButtons />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
        <p className="hud-tag mb-3">Moduli</p>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-10 max-w-2xl">Un'unica plancia di comando per tutta la tua AI</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4">
          {FEATURES.map(({ Icon, t, d }, i) => (
            <div key={t} data-testid={`feature-card-${i}`} className={`hud-card hud-corners p-6 ${i < 2 ? "lg:col-span-3" : "lg:col-span-2"}`}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-cyan-500/10 border border-cyan-400/30 text-cyan-300 mb-4"><Icon size={20} /></div>
              <h3 className="font-heading text-xl font-semibold text-white tracking-wide">{t}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="sicurezza" data-testid="privacy-security-section" className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
        <div className="hud-card p-6 sm:p-10 scan-line">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 items-start">
            <div>
              <p className="hud-tag mb-3">🔐 Protocollo sicurezza · livello massimo</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white">Privacy assoluta. <span className="text-aurora">I tuoi dati, sotto il tuo controllo.</span></h2>
              <p className="mt-4 text-sm text-slate-400">Aurora ON è progettata privacy-first: crittografia in transito e a riposo, autodistruzione delle conversazioni e nessun dato venduto. Ciò che dici ad Aurora resta tra te e Aurora.</p>
              <div className="mt-5 flex flex-wrap gap-2 font-mono text-[11px]">
                {["AES-256-GCM", "TLS / HTTPS", "bcrypt", "Autodistruzione", "Nessun log venduto"].map((b) => <span key={b} className="rounded-full border border-emerald-400/30 text-emerald-200 px-3 py-1">{b}</span>)}
              </div>
              <Link to="/privacy-policy" data-testid="link-privacy" className="inline-flex mt-6 items-center gap-2 text-cyan-300 text-sm hover:text-amber-300 transition-colors">Leggi l'informativa privacy <ArrowRight size={14} /></Link>
            </div>
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {SECURITY.map(({ Icon, t, d }) => (
                <div key={t} className="rounded-xl border border-emerald-400/15 bg-[#050b18]/70 p-5">
                  <Icon className="text-emerald-300 mb-3" size={22} />
                  <h3 className="font-heading text-lg font-semibold text-white">{t}</h3>
                  <p className="text-sm text-slate-400 mt-1">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <GroupProjects />

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-16 text-center">
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white">Attiva Aurora oggi</h2>
        <p className="mt-3 text-slate-400">Installala sul telefono come un'app: dal browser tocca “Aggiungi a schermata Home”.</p>
        <Link to={startTo} data-testid="btn-start-now-bottom" className="btn-aurora inline-flex mt-8 rounded-full px-10 py-3.5 text-sm uppercase">{paid ? "Inizia la prova gratuita" : "Inizia gratis"}</Link>
      </section>
      <Footer />
    </div>
  );
}
